/**
 * translations.js
 * Toutes les chaînes de texte et données statiques en anglais et en français.
 * Usage : const t = translations[currentLanguage];
 */

export const translations = {

  /* ================================================================
     ENGLISH
  ================================================================ */
  en: {

    /* ---- Navigation ---- */
    nav: {
      aboutUs:            "About us",
      ourMission:         "Our Mission",
      ourVision:          "Our Vision",
      organization:       "Organization",
      members:            "Members",
      steeringCommittee:  "Steering Committee",
      partners:           "Partners",
      unitedNations:      "United Nations",
      spt:                "Subcommittee on Prevention of Torture (SPT)",
      cpt:                "European Committee for the Prevention of Torture (CPT)",
      foundingDocuments:  "Founding documents",
      newsEvents:         "News & Events",
      permanentSecretariat: "Permanent Secretariat",
    },

    /* ---- Hero ---- */
    hero: {
      welcome:        "Welcome to ANPMN",
      subtitle:       "Explore our vision, structure, and members, and discover the latest updates, news, and events from the Network.",
      readFullArticle: "Read Full Article",
      recentNews:     "Recent News",
      viewAllNews:    "View All News",
      scrollToExplore: "Scroll to explore",
      loading:        "Loading articles...",
    },

    /* ---- Leadership ---- */
    leadership: {
      sectionTitle:    "LEADERSHIP",
      chair:           "Chair",
      viceChair:       "Vice-Chair",
      distinctions:    "Distinctions & Achievements",
      wordByChair:     "Word of the Chair",
      wordByViceChair: "Word of the Vice-Chair",
      readMore:        "Read more",
      readLess:        "Read less",
    },

    /* ---- About slides ---- */
    aboutSlides: [
      {
        id: 'mission',
        title: 'Our mission',
        subtitle: 'Torture Prevention',
        description: 'We are committed to coordinating joint torture prevention efforts across the African continent by fostering cooperation, sharing expertise and best practices, and building capacities.',
        image: '3c.png',
        bgImage: '0c.png',
        features: [
          { title: 'Promote', description: 'Promote a unified African action against torture through effective cooperation among NPMs.' },
          { title: 'Support', description: 'Support and empower African NPMs by enhancing their independence, effectiveness, and technical capacities.' },
          { title: 'Foster',  description: 'Foster regional solidarity and institutional resilience by building sustainable, African-led structures for the prevention of torture.' },
        ],
      },
      {
        id: 'vision',
        title: 'Our vision',
        subtitle: 'Zero Torture',
        description: 'We aspire to an Africa where every individual is protected from torture and ill-treatment, where NPMs work together as one, and where human dignity, justice, and human rights are safeguarded in every corner of the continent.',
        image: '3c16.png',
        bgImage: '0c.png',
        features: [
          { title: 'A torture-free Africa',          description: "An Africa where every person's dignity and fundamental rights are upheld without exception." },
          { title: 'A strong, independent network',  description: 'A network of NPMs that sets regional standards and drives innovation in prevention and protection.' },
          { title: 'A model of African-led cooperation', description: 'A unified approach that strengthens collective action in the global fight against torture and all forms of ill-treatment.' },
        ],
      },
    ],

    /* ---- KPI ---- */
    kpi: {
      sectionTitle: "KEY FIGURES",
      items: [
        { number: "15",  label: "NPMs",                  description: "NPMs across Africa working together to prevent torture and safeguard human dignity continentally." },
        { number: "32",  label: "Visits",                description: "Monitoring conditions of individuals in places of deprivation of liberty." },
        { number: "36",  label: "Reports",               description: "Providing detailed insights into NPM activities, findings from visits as well as thematic publications on broader issues related to torture prevention." },
        { number: "235", label: "Recommendations",       description: "To enhance torture prevention action in Africa and promote practice that align with international standards." },
      ],
    },

    /* ---- Members ---- */
    members: {
      sectionTitle: "MEMBERS",
      description: "The Network brings together 16 NPMs representing all five regions of the African continent, united in their commitment to achieve a zero-torture objective and uphold human dignity.",
    },

    /* ---- Steering Committee ---- */
    steeringCommittee: {
      sectionTitle: "STEERING COMMITTEE",
      biography: "Biography",
      readMore: "Read more",
      readLess: "Read less",
    },

    /* ---- Presidential data ---- */
    presidentData: {
      name:        "Amina Bouayach",
      title:       "Chair of the ANPMN",
      image:       "12.png",
      credentials: "Chairperson of Morocco's National Human Rights Council (CNDH)",
      shortMessage: "The establishment of the African National Preventive Mechanisms Network (ANPMN) is a landmark and visionary achievement that embodies Africa's united resolve to eradicate torture.",
      fullMessage: `The establishment of the African National Preventive Mechanisms Network (ANPMN) is a landmark achievement reflecting Africa's united resolve to eliminate torture. Rooted in the collective determination of African human rights institutions and anchored in the Marrakech Declaration, the ANPMN is a powerful testament to our shared commitment to reinforcing continental cooperation and safeguarding human dignity across our African continent.

With its Permanent Secretariat based at Morocco's National Human Rights Institution in Rabat, the Network is positioned to strengthen and expand as a strong, continental mechanism rooted in African ownership, expertise, and solidarity. More than a coordinating body, the Network is a platform for collective advocacy, policy leadership, and strategic engagement at both regional and international levels.

The fight against torture is not abstract; it touches lives, families, and communities across Africa. We owe it to the survivors, the vulnerable, and future generations to be relentless in this mission. The African Network of NPMs is a force, uniting us in purpose and action. It calls on each of us to uphold the highest standards of justice and humanity and to refuse complacency in the face of abuse.

I have always wholeheartedly believed that the path to a torture-free Africa lies through strong, institutional African-African cooperation, and this Network is the embodiment of that path. It is our collective voice and our united stand against torture. Together, we will transform the promise of the Marrakech Declaration into a lasting reality where dignity is protected, and human rights are defended.`,
      achievements: [
        "First woman to chair the Global Alliance of National Human Rights Institutions (GANHRI)",
        "First woman to chair Morocco's national human rights institution",
        "Received the 2023 North-South Prize of the CoE for her dedication to promoting human rights and preventing torture across Africa",
        "Recognized by the OHCHR as one of five women HRDs driving a more equitable post-COVID world",
      ],
      quote: "\u201cThe ANPMN embodies a shared vision for a continent that upholds human rights, protects dignity, and advances sustainable African-African cooperation toward a torture-free Africa.\u201d",
    },

    vicePresidentData: {
      name:        "Chris Nissen",
      title:       "Vice-Chair of the ANPMN",
      image:       "30.png",
      credentials: "Chairperson of the South African Human Rights Commission (SAHRC)",
      shortMessage: "[...]",
      fullMessage:  "[...]",
      achievements: [
        "Awarded the Professional Management Review Golden Arrow Award for the Best Political Personality in 1998",
        "Honoured with streets and a school named after him in Knysna for his contributions to human rights promotion",
        "He was appointed as a member of the Western Cape Provincial Legislature, served as Deputy Speaker and as MEC of Economic Affairs",
        "Community organiser in communities such as Graaf Reinett, Eerste River Advice Office, Macassar, Die Bos (now named after him as, Chris Nissen Park) and Sir Lowry's Pass Village",
      ],
      quote: "[...]",
    },

    /* ---- Steering members ---- */
    steeringMembersData: [
      {
        name: "Amina Bouayach",
        role: "Chair",
        organization: "National Human Rights Institution of Morocco",
        country: "Morocco",
        flag: "maroc.jpg",
        image: "12.png",
        hasBiography: true,
        shortBio: "Chairperson of Morocco's NHRI, Amina Bouayach is a prominent human rights defender. She currently chairs GANHRI, the African Network of National Preventive Mechanisms against Torture, and NANHRI's WG on Migration.",
        fullBio: "Chairperson of Morocco's NHRI, Amina Bouayach is a prominent human rights defender. She currently chairs GANHRI, the African Network of National Preventive Mechanisms against Torture, and NANHRI's WG on Migration. Leading a remarkable human rights career, she has been at the heart of transformative reforms in Morocco, including transitional justice, constitutional review, family code revisions, judicial and penal code reforms. Her unwavering commitment and dedication to promoting justice, dignity, and equality across the globe has earned her several distinguished international awards. Fluent in five languages, she previously served as Morocco's Ambassador to Sweden and Latvia and as SG of the International Federation for Human Rights. She has been an influential member of several human rights committees and groups and was the first woman elected to chair a major human rights organization in Morocco.",
      },
      {
        name: "Chris Nissen",
        role: "Vice-Chair",
        organization: "Chairperson of the South African Human Rights Commission",
        country: "South Africa",
        flag: "southafrica.jpg",
        image: "30.png",
        hasBiography: true,
        shortBio: "Chairperson of the South African Human Rights Commission, Chris Nissen holds a Bachelor of Arts, a Master of Arts in Humanities and a Diploma in Theology.",
        fullBio: "Chairperson of the South African Human Rights Commission, Chris Nissen holds a Bachelor of Arts, a Master of Arts in Humanities and a Diploma in Theology. Ordained as a Minister of the Uniting Presbyterian Church of South Africa in 1981, Mr. Nissen served congregations across diverse regions. In the 1980s, he played a leading role at the Western Province Council of Churches, spearheading the Standing for the Truth Campaign. Throughout the 1980s and 1990s, Mr. Nissen was deeply involved in community organizing. He championed essential human rights causes, including housing and land access. His dedication to the development of local communities earned him the recognition of having streets and a school named after him in Knysna. He was appointed as a Member of the Western Cape Provincial Legislature, served as Deputy Speaker, and was MEC of Economic Affairs.",
      },
      { name: "Seth Andriamarohasina", role: "Member", organization: "The National Independent Human Rights Commission (CNIDH) of Madagascar", country: "Madagascar", flag: "madagascar.jpg", image: "32.png", hasBiography: true, shortBio: "Seth Andriamarohasina is the President of the National Independent Human Rights Commission (CNIDH) of Madagascar." },
      { name: "Cheikh Ahmed Ould Zahaf", role: "Member", organization: "The National Mechanism for the Prevention of Torture (MNP) of Mauritania", country: "Mauritania", flag: "mauritania.jpg", image: "33.png", hasBiography: true, shortBio: "Cheikh Ahmed Ould Zahaf serves as the President of the National Mechanism for the Prevention of Torture (MNP) of Mauritania." },
      { name: "Madiaw Diaw", role: "Member", organization: "National Observer of Places of Deprivation of Liberty (ONLPL) of Senegal", country: "Senegal", flag: "senegal.jpg", image: "31.png", hasBiography: true, shortBio: "Madiaw Diaw serves as the National Observer of Places of Deprivation of Liberty (ONLPL) of Senegal." },
      { name: "Eurídice Mascarenhas", role: "Member", organization: "The National Commission for Human Rights and Citizenship (CNDHC) of Cabo Verde", country: "Cabo Verde", flag: "capvert.jpg", image: "34.png", hasBiography: true, shortBio: "Eurídice Mascarenhas serves as the President of the National Commission for Human Rights and Citizenship (CNDHC) of Cabo Verde." },
      { name: "Albachir Macassar", role: "Member", organization: "The National Human Rights Commission of Mozambique", country: "Mozambique", flag: "mozanbique.jpg", image: "35.png", hasBiography: true, shortBio: "Albachir Macassar serves as the Chairperson of the National Human Rights Commission of Mozambique." },
    ],

    /* ---- Marrakech documents ---- */
    marrakech: {
      keyHighlights:    "Key Highlights",
      officialDocument: "Official Document",
      downloadPDF:      "Download PDF",
      viewDocument:     "View document",
      prevDoc:          "Previous document",
      nextDoc:          "Next document",
    },

    marrakechDocuments: [
      {
        id: 1, bannerImage: "28.jpg",
        title: "Creation of the ANPMN",
        subtitle: "The Declaration reflects a strong collective commitment to preventing torture across the African continent through NHRIs that host NPMs as well as independent NPMs",
        date: "June 2023", location: "Marrakech, Morocco",
        description: "In June 2023, Morocco's NHRI (CNDH) hosted the international conference \"Practices of the NPMs in Africa: Challenges and Emerging Opportunities.\" The event culminated in the adoption of the Marrakech Declaration on the Prevention of torture in Africa, which called for the establishment of a Network of African NPMs. The Declaration reflects a strong collective commitment to preventing torture across the African continent through NHRIs that host NPMs as well as independent NPMs.",
        highlights: ["25 African countries participated", "Establishment of the Permanent Secretariat in Rabat", "New cooperation framework established", "Commitment to capacity building programs"],
        documentInfo: { title: "Creation of the ANPMN", pages: "4 pages", language: "English", fileSize: "0.15 MB", downloadUrl: "/docs/1.pdf" },
        eventImages: [{ src: "25.jpg", title: "Declaration Signing Ceremony", description: "Representatives from 25 African countries sign the historic declaration" }, { src: "26.jpg", title: "Summit Participants", description: "Group photo of all participating delegates and officials" }, { src: "27.jpg", title: "Summit Venue", description: "The beautiful venue in Marrakech hosting this historic event" }],
      },
      {
        id: 2, bannerImage: "29.jpg",
        title: "ANPMN Statute",
        subtitle: "Statute Of The African National Preventive Mechanisms Network",
        date: "June 2024", location: "Cape Town, South Africa",
        description: "The Second African International Conference on NPMs, held on June 26-27, 2024 in Cape Town, South Africa, ratified the statute of the ANPMN as a regional institutional framework that brings together NHRIs hosting NPMs and independent NPMs, aiming to prevent torture across the continent.",
        highlights: ["A peer network for National Preventive Mechanisms in Africa", "A General Assembly (highest decision-making body), exercising general oversight of the Network", "5 Board members, taking into consideration regional and linguistic representation in Africa", "A Permanent Secretariat in Rabat, Morocco"],
        documentInfo: { title: "ANPMN Statute", pages: "10 pages", language: "English", fileSize: "0.19 MB", downloadUrl: "/docs/2.pdf" },
        eventImages: [{ src: "30.jpg", title: "Legal Registration", description: "Official registration ceremony" }, { src: "31.jpg", title: "Headquarters Opening", description: "Grand opening of the headquarters in Rabat" }, { src: "32.jpg", title: "Board Installation", description: "Installation ceremony of the first governing board members" }],
      },
      {
        id: 3, bannerImage: "33.jpg",
        title: "ANPMN Internal regulations",
        subtitle: "Internal Regulations of the African National Preventive Mechanisms Network (ANPMN)",
        date: "June 2025", location: "Praia, Cabo Verde",
        description: "This document outlines the specific rules and procedures governing the operations, meetings, and administration of the ANPMN, providing clarity and transparency for its various structures.",
        highlights: ["Adoption of the agenda", "Staff positions within the Permanent Secretariat", "Budget and financial report reporting"],
        documentInfo: { title: "ANPMN Internal regulations", pages: "8 pages", language: "English", fileSize: "0.16 MB", downloadUrl: "/docs/3.pdf" },
        eventImages: [{ src: "34.jpg", title: "Regulations Approval" }, { src: "35.jpg", title: "Staff Training Session" }, { src: "36.jpg", title: "Implementation Workshop" }],
      },
      {
        id: 4, bannerImage: "33.jpg",
        title: "ANPMN 3rd Conference",
        subtitle: "Third Conference of African National Preventive Mechanisms Network",
        date: "June 2025", location: "Praia, Cabo Verde",
        description: "The Third Conference of the African NPM Network, organised by the Cabo Verde NPM in partnership with the South African Human Rights Commission and the Moroccan National Human Rights Council, was held in Praia, Cabo Verde, on 25–26 June 2025. It focused on the use of technology in torture prevention and on strengthening the Network's organizational development.",
        highlights: ["Focus on the responsible use of technology in torture prevention", "Adoption of the Network's Internal Regulations", "Approval of the action plan and digital tools", "Reaffirmation of the absolute prohibition of torture"],
        documentInfo: { title: "ANPMN 3rd Conference", pages: "8 pages", language: "English", fileSize: "0.13 MB", downloadUrl: "/docs/4.pdf" },
        eventImages: [{ src: "34.jpg", title: "Regulations Approval" }, { src: "35.jpg", title: "Staff Training Session" }, { src: "36.jpg", title: "Implementation Workshop" }],
      },
      {
        id: 5, bannerImage: "33.jpg",
        title: "The United Nations Convention Against Torture - CAT",
        subtitle: "United Nations Convention Against Torture",
        date: "December 1984", location: "Geneva",
        description: "Africa is on the verge of achieving regional universality of the CAT, with 52 of the 54 African countries already party to the treaty. The ANPMN serves as a regional platform that unites NPMs across the continent, ensuring their torture prevention action is coordinated, strengthened, and aligned with the CAT's obligations.",
        highlights: ["Absolute prohibition against torture in all circumstances", "States must prevent torture within their jurisdiction", "No deportation where torture risk exists", "Entry into force: 26 June 1987"],
        documentInfo: { title: "The United Nations Convention Against Torture (CAT)", pages: "24 pages", language: "English", fileSize: "4.1 MB", downloadUrl: "https://www.ohchr.org/en/instruments-mechanisms/instruments/convention-against-torture-and-other-cruel-inhuman-or-degrading" },
        eventImages: [{ src: "34.jpg" }, { src: "35.jpg" }, { src: "36.jpg" }],
      },
      {
        id: 6, bannerImage: "33.jpg",
        title: "Optional Protocol to the UN Convention against Torture - OPCAT",
        subtitle: "Optional Protocol to the UN Convention against Torture",
        date: "December 2022", location: "Geneva",
        description: "As of 2025, 24 African countries have ratified the OPCAT and 16 NPMs have been established. By fostering cooperation, sharing best practices, and engaging in collective advocacy, the ANPMN helps ensure that African NPMs operate in line with OPCAT standards.",
        highlights: ["Establishes international inspection system for detention places", "Creates National Preventive Mechanisms for regular monitoring", "Prevents torture through proactive oversight and recommendations", "Entry into force: 22 June 2006"],
        documentInfo: { title: "Optional Protocol to the UN Convention against Torture (OPCAT)", pages: "24 pages", language: "English", fileSize: "4.1 MB", downloadUrl: "https://www.ohchr.org/en/instruments-mechanisms/instruments/optional-protocol-convention-against-torture-and-other-cruel" },
        eventImages: [{ src: "34.jpg" }, { src: "35.jpg" }, { src: "36.jpg" }],
      },
    ],

    /* ---- News ---- */
    news: {
      sectionTitle: "NEWS & EVENTS",
      readFullArticle: "Read Full Article",
    },

    /* ---- News Page ---- */
    newsPage: {
      heroSubtitle:       "Stay updated with the latest developments in human rights and preventive mechanisms",
      totalArticles:      "Total Articles",
      topics:             "Topics",
      searchPlaceholder:  "Search articles by title, content, or tags...",
      filters:            "Filters",
      clearAll:           "Clear All",
      category:           "Category",
      tags:               "Tags",
      sortBy:             "Sort By",
      newestFirst:        "Newest First",
      alphabetical:       "Alphabetical",
      activeFilters:      "Active filters:",
      categoryPrefix:     "Category:",
      searchPrefix:       "Search:",
      showing:            "Showing",
      of:                 "of",
      articlesLabel:      "articles",
      loadingArticles:    "Loading articles...",
      noArticlesFound:    "No articles found",
      tryAdjusting:       "Try adjusting your filters or search query",
      clearAllFilters:    "Clear All Filters",
      exploreMore:        "Explore More",
      discoverMore:       "Discover more about our mission and initiatives",
      returnToWebsite:    "Return to Website",
      readMore:           "Read More",
    },

    /* ---- Article Page ---- */
    articlePage: {
      backToNews:              "Back to News",
      views:                   "views",
      previousArticle:         "Previous article",
      nextArticle:             "Next article",
      recommendedArticles:     "Recommended articles",
      shareArticle:            "Share this article",
      shareOnFacebook:         "Share on Facebook",
      shareOnTwitter:          "Share on Twitter",
      shareOnLinkedIn:         "Share on LinkedIn",
      copyLink:                "Copy link",
      linkCopied:              "Link copied to clipboard!",
      loading:                 "Loading article...",
      notFound:                "Article not found",
      notFoundMessage:         "The article you are looking for does not exist.",
      readingComplete:         "Reading complete",
      readingCompleteMessage:  "You've listened to the entire article",
      ttsReady:                "Ready to read...",
      ttsVoice:                "Voice",
      ttsSpeed:                "Speed",
      ttsSentences:            "sentences",
      ttsInitializing:         "The audio reading system is initializing. Please wait.",
    },

    /* ---- Secretariat ---- */
    secretariat: {
      sectionTitle: "Permanent Secretariat of the ANPMN",
      subtitle:     "Rabat, Morocco",
      description:  "The Permanent Secretariat coordinates continental efforts to prevent torture and safeguard human dignity across Africa.",
      features: [
        { title: "Coordinating efforts",        description: "Bringing together African NPMs to foster collaboration and strengthen collective action against torture." },
        { title: "Building strong institutions", description: "Supporting NPMs to enhance their independence, effectiveness, and sustainability." },
        { title: "Empowering through exchange",  description: "Continuous best practice and knowledge-exchange opportunities to develop expertise in torture prevention and human rights monitoring." },
        { title: "Enhancing advocacy",           description: "Amplifying the collective voice of NPMs to shape policies and protect human rights globally." },
      ],
      contact: {
        addressTitle:  "Address",
        addressLine1:  "Permanent Secretariat of the African NPMN",
        addressLine2:  "N° 22 Avenue Riad Hay Riad BP 21527, Rabat",
        phoneTitle:    "Phone",
        phone:         "+212 5 37 54 00 00",
        emailTitle:    "Email",
        email:         "permanentsecretariat@africannpms.org",
        hoursTitle:    "Opening Hours",
        hours:         "Monday - Friday: 9:00 - 17:00",
      },
    },

    /* ---- Footer ---- */
    footer: {
      tagline:     "African National Preventive Mechanisms Network",
      description: "We are committed to coordinating joint torture prevention efforts across the African continent by fostering cooperation, sharing expertise and best practices, and building capacities.",
      quickLinks:  "Quick Links",
      ourMissions: "Our mission",
      contact:     "Contact",
      aboutUs:     "About Us",
      members:     "Members",
      newsEvents:  "News & Events",
      secretariat: "Permanent Secretariat",
      promote:     "Promote",
      support:     "Support",
      foster:      "Foster",
      address:     "Permanent Secretariat, Rabat, Morocco",
      legalNotice: "Legal Notice",
      privacy:     "Privacy Policy",
      copyright:   "© 2025 ANPMN. All rights reserved.",
    },
  },

  /* ================================================================
     FRANÇAIS
  ================================================================ */
  fr: {

    /* ---- Navigation ---- */
    nav: {
      aboutUs:            "À propos",
      ourMission:         "Notre Mission",
      ourVision:          "Notre Vision",
      organization:       "Organisation",
      members:            "Membres",
      steeringCommittee:  "Comité Directeur",
      partners:           "Partenaires",
      unitedNations:      "Nations Unies",
      spt:                "Sous-comité pour la prévention de la torture (SPT)",
      cpt:                "Comité européen pour la prévention de la torture (CPT)",
      foundingDocuments:  "Documents fondateurs",
      newsEvents:         "Actualités & Événements",
      permanentSecretariat: "Secrétariat Permanent",
    },

    /* ---- Hero ---- */
    hero: {
      welcome:        "Bienvenue à l'ANPMN",
      subtitle:       "Explorez notre vision, notre structure et nos membres, et découvrez les dernières actualités, nouvelles et événements du Réseau.",
      readFullArticle: "Lire l'article complet",
      recentNews:     "Actualités récentes",
      viewAllNews:    "Voir toutes les actualités",
      scrollToExplore: "Défiler pour explorer",
      loading:        "Chargement des articles...",
    },

    /* ---- Leadership ---- */
    leadership: {
      sectionTitle:    "LEADERSHIP",
      chair:           "Présidente",
      viceChair:       "Vice-Président",
      distinctions:    "Distinctions & Réalisations",
      wordByChair:     "Mot de la Présidente",
      wordByViceChair: "Mot du Vice-Président",
      readMore:        "Lire plus",
      readLess:        "Lire moins",
    },

    /* ---- About slides ---- */
    aboutSlides: [
      {
        id: 'mission',
        title: 'Notre mission',
        subtitle: 'Prévention de la torture',
        description: "Nous nous engageons à coordonner les efforts conjoints de prévention de la torture à travers le continent africain en favorisant la coopération, le partage d'expertise et des meilleures pratiques, et le renforcement des capacités.",
        image: '3c.png',
        bgImage: '0c.png',
        features: [
          { title: 'Promouvoir', description: "Promouvoir une action africaine unifiée contre la torture grâce à une coopération efficace entre les MNP." },
          { title: 'Soutenir',   description: "Soutenir et autonomiser les MNP africains en renforçant leur indépendance, leur efficacité et leurs capacités techniques." },
          { title: 'Favoriser',  description: "Favoriser la solidarité régionale et la résilience institutionnelle en construisant des structures africaines durables pour la prévention de la torture." },
        ],
      },
      {
        id: 'vision',
        title: 'Notre vision',
        subtitle: 'Zéro torture',
        description: "Nous aspirons à une Afrique où chaque individu est protégé contre la torture et les mauvais traitements, où les MNP travaillent ensemble comme un seul, et où la dignité humaine, la justice et les droits de l'homme sont sauvegardés dans chaque coin du continent.",
        image: '3c16.png',
        bgImage: '0c.png',
        features: [
          { title: 'Une Afrique sans torture',               description: "Une Afrique où la dignité et les droits fondamentaux de chaque personne sont respectés sans exception." },
          { title: 'Un réseau fort et indépendant',          description: "Un réseau de MNP qui fixe des normes régionales et stimule l'innovation en matière de prévention et de protection." },
          { title: 'Un modèle de coopération africaine',     description: "Une approche unifiée qui renforce l'action collective dans la lutte mondiale contre la torture et toutes formes de mauvais traitements." },
        ],
      },
    ],

    /* ---- KPI ---- */
    kpi: {
      sectionTitle: "CHIFFRES CLÉS",
      items: [
        { number: "15",  label: "MNP",                  description: "MNP à travers l'Afrique travaillant ensemble pour prévenir la torture et sauvegarder la dignité humaine à l'échelle continentale." },
        { number: "32",  label: "Visites",               description: "Surveillance des conditions de détention des personnes privées de liberté." },
        { number: "36",  label: "Rapports",              description: "Fournissant des informations détaillées sur les activités des MNP, les résultats des visites ainsi que des publications thématiques sur des questions plus larges liées à la prévention de la torture." },
        { number: "235", label: "Recommandations",       description: "Pour renforcer l'action de prévention de la torture en Afrique et promouvoir des pratiques conformes aux normes internationales." },
      ],
    },

    /* ---- Members ---- */
    members: {
      sectionTitle: "MEMBRES",
      description:  "Le Réseau rassemble 16 MNP représentant les cinq régions du continent africain, unis dans leur engagement à atteindre l'objectif zéro torture et à défendre la dignité humaine.",
    },

    /* ---- Steering Committee ---- */
    steeringCommittee: {
      sectionTitle: "COMITÉ DIRECTEUR",
      biography:    "Biographie",
      readMore:     "Lire plus",
      readLess:     "Réduire",
    },

    /* ---- Presidential data ---- */
    presidentData: {
      name:        "Amina Bouayach",
      title:       "Présidente de l'ANPMN",
      image:       "12.png",
      credentials: "Présidente du Conseil National des Droits de l'Homme du Maroc (CNDH)",
      shortMessage: "La création du Réseau Africain des Mécanismes Nationaux de Prévention (ANPMN) est une réalisation fondatrice et visionnaire qui incarne la résolution unie de l'Afrique à éradiquer la torture.",
      fullMessage: `La création du Réseau Africain des Mécanismes Nationaux de Prévention (ANPMN) est une réalisation fondatrice qui reflète la résolution unie de l'Afrique d'éliminer la torture. Enraciné dans la détermination collective des institutions africaines des droits de l'homme et ancré dans la Déclaration de Marrakech, l'ANPMN témoigne puissamment de notre engagement commun à renforcer la coopération continentale et à sauvegarder la dignité humaine à travers notre continent africain.

Avec son Secrétariat Permanent établi au sein de l'Institution Nationale des Droits de l'Homme du Maroc à Rabat, le Réseau est positionné pour se renforcer et s'élargir en tant que mécanisme continental solide, enraciné dans l'appropriation, l'expertise et la solidarité africaines. Plus qu'un organe de coordination, le Réseau est une plateforme de plaidoyer collectif, de leadership politique et d'engagement stratégique aux niveaux régional et international.

La lutte contre la torture n'est pas abstraite ; elle touche des vies, des familles et des communautés à travers l'Afrique. Nous devons aux survivants, aux personnes vulnérables et aux générations futures d'être inébranlables dans cette mission. Le Réseau Africain des MNP est une force, nous unissant dans notre objectif et notre action. Il nous appelle chacun à défendre les normes les plus élevées de justice et d'humanité et à refuser la complaisance face aux abus.

J'ai toujours cru de tout cœur que le chemin vers une Afrique sans torture passe par une coopération institutionnelle africaine forte, et ce Réseau en est l'incarnation. C'est notre voix collective et notre position unie contre la torture. Ensemble, nous transformerons la promesse de la Déclaration de Marrakech en une réalité durable où la dignité est protégée et les droits de l'homme défendus.`,
      achievements: [
        "Première femme à présider l'Alliance Mondiale des Institutions Nationales des Droits de l'Homme (GANHRI)",
        "Première femme à présider l'institution nationale des droits de l'homme du Maroc",
        "Lauréate du Prix Nord-Sud 2023 du Conseil de l'Europe pour son dévouement à la promotion des droits de l'homme et à la prévention de la torture en Afrique",
        "Reconnue par le HCDH comme l'une des cinq femmes défenseures des droits de l'homme œuvrant pour un monde post-COVID plus équitable",
      ],
      quote: "« L'ANPMN incarne une vision partagée pour un continent qui défend les droits de l'homme, protège la dignité et fait avancer la coopération africaine durable vers une Afrique sans torture. »",
    },

    vicePresidentData: {
      name:        "Chris Nissen",
      title:       "Vice-Président de l'ANPMN",
      image:       "30.png",
      credentials: "Président de la Commission Sud-Africaine des Droits de l'Homme (SAHRC)",
      shortMessage: "[...]",
      fullMessage:  "[...]",
      achievements: [
        "Lauréat du Prix Golden Arrow de Professional Management Review pour la Meilleure Personnalité Politique en 1998",
        "Honoré par des rues et une école portant son nom à Knysna pour ses contributions à la promotion des droits de l'homme",
        "Nommé membre de l'Assemblée législative provinciale du Cap-Occidental, il a exercé les fonctions de vice-président et de MEC des Affaires économiques",
        "Organisateur communautaire dans des communautés telles que Graaf Reinett, Eerste River Advice Office, Macassar et Sir Lowry's Pass Village",
      ],
      quote: "[...]",
    },

    /* ---- Steering members (FR) ---- */
    steeringMembersData: [
      {
        name: "Amina Bouayach",
        role: "Présidente",
        organization: "Institution Nationale des Droits de l'Homme du Maroc",
        country: "Maroc",
        flag: "maroc.jpg", image: "12.png", hasBiography: true,
        shortBio: "Présidente de l'INDH du Maroc, Amina Bouayach est une éminente défenseure des droits de l'homme. Elle préside actuellement le GANHRI, le Réseau Africain des MNP contre la torture et le groupe de travail de NANHRI sur les migrations.",
        fullBio: "Présidente de l'INDH du Maroc, Amina Bouayach est une éminente défenseure des droits de l'homme. Elle préside actuellement le GANHRI, le Réseau Africain des MNP contre la torture et le groupe de travail de NANHRI sur les migrations. À la tête d'une remarquable carrière dans les droits de l'homme, elle a été au cœur de réformes transformatrices au Maroc, notamment en matière de justice transitionnelle, de révision constitutionnelle, de réformes du code de la famille, du judiciaire et du code pénal. Son engagement indéfectible a valu à plusieurs récompenses internationales distinguées. Polyglotte parlant cinq langues, elle a précédemment servi comme Ambassadrice du Maroc en Suède et en Lettonie et comme Secrétaire Générale de la Fédération Internationale des droits de l'homme.",
      },
      {
        name: "Chris Nissen",
        role: "Vice-Président",
        organization: "Président de la Commission Sud-Africaine des Droits de l'Homme",
        country: "Afrique du Sud",
        flag: "southafrica.jpg", image: "30.png", hasBiography: true,
        shortBio: "Président de la Commission Sud-Africaine des Droits de l'Homme, Chris Nissen est titulaire d'un Bachelor of Arts, d'un Master of Arts en Humanités et d'un Diplôme en Théologie.",
        fullBio: "Président de la Commission Sud-Africaine des Droits de l'Homme, Chris Nissen est titulaire d'un Bachelor of Arts, d'un Master of Arts en Humanités et d'un Diplôme en Théologie. Ordonné ministre de l'Église presbytérienne unissante d'Afrique du Sud en 1981, M. Nissen a servi des congrégations dans diverses régions. Dans les années 1980, il a joué un rôle de premier plan au Conseil des Églises de la province du Cap-Occidental. Il a été nommé membre de l'Assemblée législative provinciale du Cap-Occidental, a exercé les fonctions de vice-président et de MEC des Affaires économiques.",
      },
      { name: "Seth Andriamarohasina", role: "Membre", organization: "Commission Nationale Indépendante des Droits de l'Homme (CNIDH) de Madagascar", country: "Madagascar", flag: "madagascar.jpg", image: "32.png", hasBiography: true, shortBio: "Seth Andriamarohasina est le Président de la Commission Nationale Indépendante des Droits de l'Homme (CNIDH) de Madagascar." },
      { name: "Cheikh Ahmed Ould Zahaf", role: "Membre", organization: "Mécanisme National de Prévention de la Torture (MNP) de Mauritanie", country: "Mauritanie", flag: "mauritania.jpg", image: "33.png", hasBiography: true, shortBio: "Cheikh Ahmed Ould Zahaf est le Président du Mécanisme National de Prévention de la Torture (MNP) de Mauritanie." },
      { name: "Madiaw Diaw", role: "Membre", organization: "Observateur National des Lieux de Privation de Liberté (ONLPL) du Sénégal", country: "Sénégal", flag: "senegal.jpg", image: "31.png", hasBiography: true, shortBio: "Madiaw Diaw est l'Observateur National des Lieux de Privation de Liberté (ONLPL) du Sénégal." },
      { name: "Eurídice Mascarenhas", role: "Membre", organization: "Commission Nationale des Droits de l'Homme et de la Citoyenneté (CNDHC) du Cabo Verde", country: "Cabo Verde", flag: "capvert.jpg", image: "34.png", hasBiography: true, shortBio: "Eurídice Mascarenhas est la Présidente de la Commission Nationale des Droits de l'Homme et de la Citoyenneté (CNDHC) du Cabo Verde." },
      { name: "Albachir Macassar", role: "Membre", organization: "Commission Nationale des Droits de l'Homme du Mozambique", country: "Mozambique", flag: "mozanbique.jpg", image: "35.png", hasBiography: true, shortBio: "Albachir Macassar est le Président de la Commission Nationale des Droits de l'Homme du Mozambique." },
    ],

    /* ---- Marrakech ---- */
    marrakech: {
      keyHighlights:    "Points Clés",
      officialDocument: "Document Officiel",
      downloadPDF:      "Télécharger PDF",
      viewDocument:     "Voir le document",
      prevDoc:          "Document précédent",
      nextDoc:          "Document suivant",
    },

    marrakechDocuments: [
      {
        id: 1, bannerImage: "28.jpg",
        title: "Création de l'ANPMN",
        subtitle: "La Déclaration reflète un engagement collectif fort pour prévenir la torture à travers le continent africain",
        date: "Juin 2023", location: "Marrakech, Maroc",
        description: "En juin 2023, l'INDH du Maroc (CNDH) a accueilli la conférence internationale « Pratiques des MNP en Afrique : Défis et Opportunités émergentes ». L'événement a abouti à l'adoption de la Déclaration de Marrakech sur la Prévention de la torture en Afrique, qui a appelé à la création d'un Réseau des MNP africains. La Déclaration reflète un engagement collectif fort pour prévenir la torture à travers le continent africain.",
        highlights: ["25 pays africains ont participé", "Création du Secrétariat Permanent à Rabat", "Nouveau cadre de coopération établi", "Engagement en faveur des programmes de renforcement des capacités"],
        documentInfo: { title: "Création de l'ANPMN", pages: "4 pages", language: "Français", fileSize: "0,15 Mo", downloadUrl: "/docs/1.pdf" },
        eventImages: [{ src: "25.jpg", title: "Cérémonie de signature" }, { src: "26.jpg", title: "Participants au Sommet" }, { src: "27.jpg", title: "Lieu du Sommet" }],
      },
      {
        id: 2, bannerImage: "29.jpg",
        title: "Statut de l'ANPMN",
        subtitle: "Statut du Réseau Africain des Mécanismes Nationaux de Prévention",
        date: "Juin 2024", location: "Le Cap, Afrique du Sud",
        description: "La Deuxième Conférence Africaine Internationale sur les MNP, tenue les 26-27 juin 2024 au Cap, en Afrique du Sud, a ratifié le statut de l'ANPMN en tant que cadre institutionnel régional rassemblant les INDH accueillant des MNP et les MNP indépendants, visant à prévenir la torture à travers le continent.",
        highlights: ["Un réseau pairs pour les MNP en Afrique", "Une Assemblée Générale (organe décisionnel suprême)", "5 membres du Conseil tenant compte de la représentation régionale et linguistique", "Un Secrétariat Permanent à Rabat, Maroc"],
        documentInfo: { title: "Statut de l'ANPMN", pages: "10 pages", language: "Français", fileSize: "0,19 Mo", downloadUrl: "/docs/2.pdf" },
        eventImages: [{ src: "30.jpg", title: "Enregistrement légal" }, { src: "31.jpg", title: "Inauguration du siège" }, { src: "32.jpg", title: "Installation du Conseil" }],
      },
      {
        id: 3, bannerImage: "33.jpg",
        title: "Règlement intérieur de l'ANPMN",
        subtitle: "Règlement intérieur du Réseau Africain des MNP (ANPMN)",
        date: "Juin 2025", location: "Praia, Cabo Verde",
        description: "Ce document décrit les règles et procédures spécifiques régissant les opérations, réunions et administration de l'ANPMN, assurant clarté et transparence pour ses diverses structures.",
        highlights: ["Adoption de l'ordre du jour", "Postes du personnel au sein du Secrétariat Permanent", "Budget et rapports financiers"],
        documentInfo: { title: "Règlement intérieur de l'ANPMN", pages: "8 pages", language: "Français", fileSize: "0,16 Mo", downloadUrl: "/docs/3.pdf" },
        eventImages: [{ src: "34.jpg" }, { src: "35.jpg" }, { src: "36.jpg" }],
      },
      {
        id: 4, bannerImage: "33.jpg",
        title: "3e Conférence de l'ANPMN",
        subtitle: "Troisième Conférence du Réseau Africain des MNP",
        date: "Juin 2025", location: "Praia, Cabo Verde",
        description: "La Troisième Conférence du Réseau Africain des MNP, organisée par le MNP du Cabo Verde en partenariat avec la Commission Sud-Africaine des Droits de l'Homme et le CNDH du Maroc, s'est tenue à Praia, Cabo Verde, les 25-26 juin 2025. Elle s'est concentrée sur l'utilisation de la technologie dans la prévention de la torture et sur le renforcement du développement organisationnel du Réseau.",
        highlights: ["Accent sur l'utilisation responsable de la technologie dans la prévention de la torture", "Adoption du Règlement intérieur du Réseau", "Approbation du plan d'action et des outils numériques", "Réaffirmation de l'interdiction absolue de la torture"],
        documentInfo: { title: "3e Conférence de l'ANPMN", pages: "8 pages", language: "Français", fileSize: "0,13 Mo", downloadUrl: "/docs/4.pdf" },
        eventImages: [{ src: "34.jpg" }, { src: "35.jpg" }, { src: "36.jpg" }],
      },
      {
        id: 5, bannerImage: "33.jpg",
        title: "La Convention des Nations Unies contre la torture - CAT",
        subtitle: "Convention des Nations Unies contre la torture",
        date: "Décembre 1984", location: "Genève",
        description: "L'Afrique est sur le point d'atteindre l'universalité régionale de la CAT, avec 52 des 54 pays africains déjà parties au traité. L'ANPMN sert de plateforme régionale unissant les MNP à travers le continent, garantissant que leur action de prévention de la torture est coordonnée, renforcée et alignée sur les obligations de la CAT.",
        highlights: ["Interdiction absolue de la torture en toutes circonstances", "Les États doivent prévenir la torture dans leur juridiction", "Pas d'expulsion en cas de risque de torture", "Entrée en vigueur : 26 juin 1987"],
        documentInfo: { title: "Convention des Nations Unies contre la torture (CAT)", pages: "24 pages", language: "Français", fileSize: "4,1 Mo", downloadUrl: "https://www.ohchr.org/fr/instruments-mechanisms/instruments/convention-against-torture-and-other-cruel-inhuman-or-degrading" },
        eventImages: [{ src: "34.jpg" }, { src: "35.jpg" }, { src: "36.jpg" }],
      },
      {
        id: 6, bannerImage: "33.jpg",
        title: "Protocole facultatif à la Convention des Nations Unies contre la torture - OPCAT",
        subtitle: "Protocole facultatif à la Convention des Nations Unies contre la torture",
        date: "Décembre 2022", location: "Genève",
        description: "En 2025, 24 pays africains ont ratifié l'OPCAT et 16 MNP ont été établis. En favorisant la coopération, le partage des meilleures pratiques et le plaidoyer collectif, l'ANPMN aide à garantir que les MNP africains opèrent conformément aux normes de l'OPCAT.",
        highlights: ["Établit un système international d'inspection des lieux de détention", "Crée des MNP pour un suivi régulier", "Prévient la torture par une surveillance proactive et des recommandations", "Entrée en vigueur : 22 juin 2006"],
        documentInfo: { title: "Protocole facultatif à la Convention contre la torture (OPCAT)", pages: "24 pages", language: "Français", fileSize: "4,1 Mo", downloadUrl: "https://www.ohchr.org/fr/instruments-mechanisms/instruments/optional-protocol-convention-against-torture-and-other-cruel" },
        eventImages: [{ src: "34.jpg" }, { src: "35.jpg" }, { src: "36.jpg" }],
      },
    ],

    /* ---- News ---- */
    news: {
      sectionTitle:    "ACTUALITÉS & ÉVÉNEMENTS",
      readFullArticle: "Lire l'article complet",
    },

    /* ---- News Page ---- */
    newsPage: {
      heroSubtitle:       "Restez informé des derniers développements en matière de droits de l'homme et de mécanismes préventifs",
      totalArticles:      "Articles au total",
      topics:             "Sujets",
      searchPlaceholder:  "Rechercher par titre, contenu ou tags...",
      filters:            "Filtres",
      clearAll:           "Tout effacer",
      category:           "Catégorie",
      tags:               "Tags",
      sortBy:             "Trier par",
      newestFirst:        "Plus récents",
      alphabetical:       "Alphabétique",
      activeFilters:      "Filtres actifs :",
      categoryPrefix:     "Catégorie :",
      searchPrefix:       "Recherche :",
      showing:            "Affichage",
      of:                 "sur",
      articlesLabel:      "articles",
      loadingArticles:    "Chargement des articles...",
      noArticlesFound:    "Aucun article trouvé",
      tryAdjusting:       "Essayez d'ajuster vos filtres ou votre recherche",
      clearAllFilters:    "Effacer tous les filtres",
      exploreMore:        "Explorer davantage",
      discoverMore:       "Découvrez notre mission et nos initiatives",
      returnToWebsite:    "Retour au site",
      readMore:           "Lire la suite",
    },

    /* ---- Article Page ---- */
    articlePage: {
      backToNews:              "Retour aux actualités",
      views:                   "vues",
      previousArticle:         "Article précédent",
      nextArticle:             "Article suivant",
      recommendedArticles:     "Articles recommandés",
      shareArticle:            "Partager cet article",
      shareOnFacebook:         "Partager sur Facebook",
      shareOnTwitter:          "Partager sur Twitter",
      shareOnLinkedIn:         "Partager sur LinkedIn",
      copyLink:                "Copier le lien",
      linkCopied:              "Lien copié dans le presse-papier !",
      loading:                 "Chargement de l'article...",
      notFound:                "Article non trouvé",
      notFoundMessage:         "L'article que vous recherchez n'existe pas.",
      readingComplete:         "Lecture terminée",
      readingCompleteMessage:  "Vous avez écouté l'article en entier",
      ttsReady:                "Prêt à lire...",
      ttsVoice:                "Voix",
      ttsSpeed:                "Vitesse",
      ttsSentences:            "phrases",
      ttsInitializing:         "Le système de lecture audio est en cours d'initialisation. Veuillez patienter.",
    },

    /* ---- Secretariat ---- */
    secretariat: {
      sectionTitle: "Secrétariat Permanent de l'ANPMN",
      subtitle:     "Rabat, Maroc",
      description:  "Le Secrétariat Permanent coordonne les efforts continentaux pour prévenir la torture et sauvegarder la dignité humaine à travers l'Afrique.",
      features: [
        { title: "Coordination des efforts",           description: "Rassembler les MNP africains pour favoriser la collaboration et renforcer l'action collective contre la torture." },
        { title: "Renforcement des institutions",       description: "Soutenir les MNP pour améliorer leur indépendance, leur efficacité et leur durabilité." },
        { title: "Renforcement par l'échange",          description: "Opportunités continues d'échange de bonnes pratiques et de connaissances pour développer l'expertise en prévention de la torture." },
        { title: "Renforcement du plaidoyer",           description: "Amplifier la voix collective des MNP pour façonner les politiques et protéger les droits de l'homme à l'échelle mondiale." },
      ],
      contact: {
        addressTitle:  "Adresse",
        addressLine1:  "Secrétariat Permanent du Réseau Africain des MNP",
        addressLine2:  "N° 22 Avenue Riad Hay Riad BP 21527, Rabat",
        phoneTitle:    "Téléphone",
        phone:         "+212 5 37 54 00 00",
        emailTitle:    "E-mail",
        email:         "permanentsecretariat@africannpms.org",
        hoursTitle:    "Horaires d'ouverture",
        hours:         "Lundi - Vendredi : 9h00 - 17h00",
      },
    },

    /* ---- Footer ---- */
    footer: {
      tagline:     "Réseau Africain des Mécanismes Nationaux de Prévention",
      description: "Nous nous engageons à coordonner les efforts conjoints de prévention de la torture à travers le continent africain en favorisant la coopération, le partage d'expertise et des meilleures pratiques, et le renforcement des capacités.",
      quickLinks:  "Liens rapides",
      ourMissions: "Notre mission",
      contact:     "Contact",
      aboutUs:     "À propos",
      members:     "Membres",
      newsEvents:  "Actualités & Événements",
      secretariat: "Secrétariat Permanent",
      promote:     "Promouvoir",
      support:     "Soutenir",
      foster:      "Favoriser",
      address:     "Secrétariat Permanent, Rabat, Maroc",
      legalNotice: "Mentions légales",
      privacy:     "Politique de confidentialité",
      copyright:   "© 2025 ANPMN. Tous droits réservés.",
    },
  },
};
